// ClipRadar website — hero v2: first light, hero panel. Message on the left;
// on the right a tilted app-window demo — REAL app structure (Windows chrome,
// mode pills, dashed scope chips, blue accents, true result-card anatomy) with
// marketing lighting outside the glass. Four demo cycles, one per capability:
// visual NL, speech quotes (highlighted transcript lines), any/all people,
// search-by-image. Pure DOM/CSS + GSAP — runs on any hardware.
// (The three.js field lives on in hero-scene.ts + git history; legacy v1 hero
// is in hero.tsx.)
import { useEffect, useLayoutEffect, useRef, useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { CRWIcon, type IconName } from '../components/icons';
import { MetalBtn } from '../components/primitives';
import { useTyping } from '../components/hooks';
import { STILLS } from '../components/stills';
import logoMarkMono from '../assets/logo-mark-mono.svg';
import '../styles/hero.css';

gsap.registerPlugin(ScrollTrigger);

interface VisualResult { img: string; pos?: string; file: string; tc: string; dur: string }
interface SpeechResult { img: string; pos?: string; file: string; tc: string; parts: [string, boolean?][] }

interface Demo {
  icon: IconName;
  chip: string;
  text: string; // what types in the bar ('' = by-image)
  mode: 'Visuals' | 'Speech';
  scope: string;
  seed?: string; // by-image: the reference frame in the bar
  people?: string; // people pill label
  count: string;
  hint?: string;
  more?: string; // ghost tile filling an odd grid: "+ N more …"
  visual?: VisualResult[];
  speech?: SpeechResult[];
}

// every result is a TRUE match for its query. The library is the app's real
// archival demo pack (NASA / National Archives, PD-safe) — collective-memory
// footage, so the results verify themselves on sight (archival re-skin,
// founder 2026-07-17). Speech lines are verbatim from the actual clips.
const DEMOS: Demo[] = [
  {
    icon: 'image', chip: 'the rocket clearing the tower', text: 'the rocket clearing the tower at liftoff',
    /* counts include the "+ N more" ghost tile — 3 shown + 4 more = 7 (the r2
       critic caught "3 matches" over a grid that visibly holds seven) */
    mode: 'Visuals', scope: 'All analyzed media', count: '7 matches · 0.34s',
    hint: 'One query: machine · moment · motion — no filename knows this',
    more: '+ 4 more launch angles',
    visual: [
      { img: STILLS.ignition, pos: '50% 45%', file: 'apollo11-liftoff-1969.mp4', tc: '00:00:24', dur: '0:11' },
      { img: STILLS.liftoff, pos: '50% 35%', file: 'apollo11-tracking-cam.mp4', tc: '00:00:33', dur: '0:08' },
      { img: STILLS.launchpad, pos: '50% 40%', file: 'apollo11-pad-39a.mp4', tc: '00:00:05', dur: '0:21' },
    ],
  },
  {
    icon: 'mic', chip: '“we choose to go to the moon”', text: '"we choose to go to the moon"',
    mode: 'Speech', scope: 'All analyzed media', count: '2 matches · 0.22s',
    speech: [
      {
        // verbatim from the demo clip's own transcript (speech.sqlite seg 63-64):
        // 00:10 "Why does Rice play Texas?" -> 00:18 the moon line, said 3x
        img: STILLS.ricespeech, pos: '50% 30%', file: 'jfk-moon-rice-1962.mp4', tc: '00:00:18',
        parts: [['…why does Rice play Texas? '], ['We choose to go to the moon', true], ['.']],
      },
      {
        img: STILLS.ricehands, pos: '50% 30%', file: 'jfk-moon-rice-1962.mp4', tc: '00:00:24',
        parts: [[''], ['We choose to go to the moon', true], [' in this decade and do the other things…']],
      },
    ],
  },
  {
    icon: 'users', chip: 'every shot of JFK', text: 'JFK',
    mode: 'Visuals', scope: 'All analyzed media', people: 'JFK · 1 person', count: '61 matches · 0.27s',
    hint: 'Name a face once — every appearance, every clip, every drive',
    more: '+ 58 more across the archive',
    visual: [
      { img: STILLS.inauguration, pos: '50% 30%', file: 'jfk-inauguration-1961.mp4', tc: '00:00:41', dur: '0:44' },
      { img: STILLS.cubaaddress, pos: '50% 35%', file: 'jfk-cuba-address-1962.mp4', tc: '00:00:07', dur: '1:00' },
      { img: STILLS.ricehands, pos: '52% 28%', file: 'jfk-moon-rice-1962.mp4', tc: '00:00:47', dur: '0:19' },
    ],
  },
  {
    icon: 'camera', chip: 'search by image', text: '',
    mode: 'Visuals', scope: 'All analyzed media', seed: STILLS.flagplant, count: '9 similar frames · 0.19s',
    hint: 'Seeded from a reference frame — no words needed',
    more: '+ 6 more like this frame',
    visual: [
      { img: STILLS.moonwalkers, pos: '50% 40%', file: 'apollo11-moonwalk-1969.mp4', tc: '00:00:52', dur: '0:14' },
      { img: STILLS.moonflag, pos: '50% 40%', file: 'apollo11-moonwalk-1969.mp4', tc: '00:01:05', dur: '0:09' },
      { img: STILLS.moonsurface, pos: '50% 45%', file: 'apollo11-moonwalk-1969.mp4', tc: '00:00:09', dur: '0:22' },
    ],
  },
];

const BADGES: { icon: IconName; label: string }[] = [
  { icon: 'lock', label: '100% local' },
  { icon: 'infinity', label: 'Own it forever' },
  { icon: 'globe', label: '110+ languages' },
];

const CYCLE_MS = 8600; // a new search every ~8.6s while idle

export function Hero() {
  const rootRef = useRef<HTMLElement | null>(null);
  const panelRef = useRef<HTMLDivElement | null>(null);
  const [active, setActive] = useState(-1); // which demo is typing
  const [shown, setShown] = useState(-1); // which demo's results fill the grid
  const [live, setLive] = useState(false);
  const userDrove = useRef(false);
  const d = active >= 0 ? DEMOS[active] : null;
  const { typed, done: typedDone } = useTyping(d?.text ?? '', { speed: 26, delay: 80 });
  const done = d ? (d.text ? typedDone : true) : false; // by-image has nothing to type

  // results land when the typing finishes — and stay until the next search lands
  useEffect(() => {
    if (done && active >= 0) {
      const land = window.setTimeout(() => setShown(active), d?.text ? 0 : 600);
      const cool = window.setTimeout(() => setLive(false), 2600);
      return () => { window.clearTimeout(land); window.clearTimeout(cool); };
    }
  }, [done, active, d]);
  const r = shown >= 0 ? DEMOS[shown] : null;

  // the grid and the speech rows differ in height — glide between them
  const resultsBox = useRef<HTMLDivElement | null>(null);
  const prevResH = useRef<number | null>(null);
  useLayoutEffect(() => {
    const el = resultsBox.current;
    if (!el) return;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    const h = el.offsetHeight;
    if (prevResH.current != null && Math.abs(h - prevResH.current) > 1) {
      gsap.fromTo(el, { height: prevResH.current, overflow: 'hidden' }, {
        height: h, duration: 0.4, ease: 'power3.inOut', overwrite: true,
        onComplete: () => { gsap.set(el, { clearProps: 'height,overflow' }); },
      });
    }
    prevResH.current = h;
  }, [shown]);

  // the idle demo loop — a user click restarts the clock so it never fights them
  const loopId = useRef<number | null>(null);
  const startLoop = () => {
    if (loopId.current != null) window.clearInterval(loopId.current);
    loopId.current = window.setInterval(() => {
      setActive((a) => (a + 1) % DEMOS.length);
      setLive(true);
    }, CYCLE_MS);
  };
  useEffect(() => {
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      setActive(0);
      return;
    }
    const first = window.setTimeout(() => { if (!userDrove.current) { setActive(0); setLive(true); } }, 1500);
    startLoop();
    return () => { window.clearTimeout(first); if (loopId.current != null) window.clearInterval(loopId.current); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // panel tilt: rests at a slight perspective, eases flat as the cursor nears.
  // The rAF loop + pointer listener run ONLY while the hero is on screen (an
  // IntersectionObserver starts/stops them), so nothing ticks after scroll-past.
  useEffect(() => {
    const root = rootRef.current, panel = panelRef.current;
    if (!root || !panel) return;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    if (!window.matchMedia('(pointer: fine)').matches) return;
    let raf = 0, running = false;
    let tx = 0, cx = 0; // 0 = resting tilt, 1 = flat (cursor on the panel)
    const onMove = (e: PointerEvent) => {
      const r2 = panel.getBoundingClientRect();
      const d2 = Math.hypot(e.clientX - (r2.left + r2.width / 2), e.clientY - (r2.top + r2.height / 2));
      tx = Math.max(0, Math.min(1, 1.25 - d2 / (r2.width * 0.9)));
    };
    const tick = () => {
      cx += (tx - cx) * 0.08;
      panel.style.transform = `perspective(1400px) rotateY(${(-7 * (1 - cx)).toFixed(2)}deg) rotateX(${(2.5 * (1 - cx)).toFixed(2)}deg)`;
      raf = requestAnimationFrame(tick);
    };
    const start = () => { if (!running) { running = true; root.addEventListener('pointermove', onMove); raf = requestAnimationFrame(tick); } };
    const stop = () => { if (running) { running = false; root.removeEventListener('pointermove', onMove); cancelAnimationFrame(raf); } };
    const io = new IntersectionObserver(([e]) => { if (e.isIntersecting) start(); else stop(); });
    io.observe(root);
    return () => { io.disconnect(); stop(); };
  }, []);

  // entrance + scroll choreography
  useLayoutEffect(() => {
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    const ctx = gsap.context(() => {
      const tl = gsap.timeline({ defaults: { ease: 'power4.out' } });
      tl.from('.crwh-eyebrow', { opacity: 0, y: 14, duration: 0.7 }, 0.1)
        .from('.crwh-maskline', { yPercent: 110, duration: 0.95, stagger: 0.12 }, 0.18)
        .from('.crwh-sub', { opacity: 0, y: 16, duration: 0.8 }, 0.55)
        .from('.crwh-ctas', { opacity: 0, y: 16, duration: 0.8 }, 0.7)
        .from('.crwh-meta', { opacity: 0, y: 10, duration: 0.8 }, 0.85)
        .from('.crwh-panelwrap', { opacity: 0, y: 30, duration: 1.1, ease: 'power3.out' }, 0.45)
        .from('.crwh-horizon', { opacity: 0, y: 110, duration: 1.6, ease: 'power3.out' }, 0.3)
        .from('.crwh-cue', { opacity: 0, duration: 1 }, 1.3);
      gsap.to('.crwh-stage', {
        opacity: 0.25, ease: 'none',
        scrollTrigger: { trigger: rootRef.current, start: 'center center', end: 'bottom top', scrub: true },
      });
      gsap.to('.crwh-grid2', {
        opacity: 0, yPercent: -6, ease: 'none',
        scrollTrigger: { trigger: rootRef.current, start: '62% center', end: 'bottom 28%', scrub: true },
      });
    }, rootRef);
    return () => ctx.revert();
  }, []);

  const tryDemo = (i: number) => {
    userDrove.current = true;
    setActive(i);
    setLive(true);
    startLoop();
  };

  // result renderers — each mode presents the way the real app does
  let results: ReactNode;
  if (!r) {
    results = [0, 1, 2, 3].map((i) => <div key={`sk-${i}`} className="crwh-res crwh-res-skel"><span></span></div>);
  } else if (r.speech) {
    results = r.speech.map((s, i) => (
      <div key={`${shown}-sp-${i}`} className="crwh-srow crwh-res-in" style={{ animationDelay: `${i * 110}ms` }}>
        <span className="crwh-srow-thumb" style={{ backgroundImage: `url(${s.img})`, backgroundPosition: s.pos }}></span>
        <span className="crwh-srow-body">
          <i className="crwh-srow-line">
            {s.parts.map(([t, hot], k) => hot ? <b key={k}>{t}</b> : <span key={k}>{t}</span>)}
          </i>
          <i className="crwh-srow-meta">{s.file} · <em>{s.tc}</em></i>
        </span>
      </div>
    ));
  } else if (r.visual) {
    const cards = r.visual.map((v, i) => (
      <div key={`${shown}-${i}`} className="crwh-res crwh-res-in" style={{ animationDelay: `${i * 90}ms` }}>
        <span className="crwh-res-thumb" style={{ backgroundImage: `url(${v.img})`, backgroundPosition: v.pos }}>
          <i className="crwh-res-check"></i>
          <i className="crwh-res-dur">{v.dur}</i>
        </span>
        <span className="crwh-res-meta">
          <i className="crwh-res-file">{v.file}</i>
          <i className="crwh-res-tc">{v.tc}</i>
        </span>
      </div>
    ));
    if (r.more && r.visual.length % 2 === 1) {
      cards.push(
        <div key={`${shown}-more`} className="crwh-res-more crwh-res-in" style={{ animationDelay: `${r.visual.length * 90}ms` }}>
          {r.more}
        </div>,
      );
    }
    results = cards;
  }

  return (
    <header className="crwh-hero" ref={rootRef}>
      {/* the stage: faint grid, a warm lamp over the panel, and the dawn rising at the floor */}
      <div className="crwh-stage" aria-hidden="true">
        <div className="crwh-stage-grid"></div>
        <div className="crwh-stage-glow"></div>
        <div className="crwh-horizon">
          <div className="crwh-horizon-glow"></div>
          <div className="crwh-horizon-haze"></div>
          <div className="crwh-horizon-line"></div>
        </div>
      </div>

      <div className="crwh-grid2">
        {/* ---- the message ---- */}
        <div className="crwh-msg">
          <span className="crw-eyebrow crwh-eyebrow"><i></i>Local AI footage search for editors</span>
          {/* the ClipRadar hero, brought back by the founder (2026-07-17) — the
              original three lines + terabytes sub; headline stays one calm
              gradient (the A1 no-accent-word ruling holds under glacier) */}
          <h1 className="crwh-h1">
            <span className="crwh-mask"><span className="crwh-maskline crw-h-grad">What if you could</span></span>
            <span className="crwh-mask"><span className="crwh-maskline crw-h-grad">just type what</span></span>
            <span className="crwh-mask"><span className="crwh-maskline crw-h-grad">you remember?</span></span>
          </h1>
          <p className="crwh-sub">
            Search terabytes of footage by what’s on screen, what was said, or who’s in frame — indexed on your machine.
          </p>
          <div className="crwh-ctas">
            {/* Primary always "Buy ClipRadar" → pricing; secondary always the trial. */}
            <a href="#pricing" style={{ textDecoration: 'none', display: 'flex' }}>
              <MetalBtn size="lg" style={{ letterSpacing: '0.08em', fontFamily: 'var(--font-mono)', textTransform: 'uppercase' }}>Buy ClipRadar</MetalBtn>
            </a>
            <Link to="/trial" className="crwh-filmlink" style={{ textDecoration: 'none' }}>
              <span className="crwh-filmlink-icon"><CRWIcon d="arrowRight" size={11} /></span>
              Start Free Trial
            </Link>
          </div>
          {/* the meta block — one composed group under a fading hairline: the
              integration signal leads (an editor's keep-reading moment), the
              trust badges follow quieter. Grouped so the entrance timeline
              raises it as ONE element (the lone Pr line used to pop in
              unanimated before everything around it). */}
          <div className="crwh-meta">
            <a href="#premiere" className="crwh-premiere">
              <span className="crwh-premiere-tile" aria-hidden="true">Pr</span>
              Works inside Premiere Pro
              <span className="crwh-premiere-arr" aria-hidden="true"><CRWIcon d="arrowRight" size={11} /></span>
            </a>
            <div className="crwh-badges">
              {BADGES.map((b) => (
                <span key={b.label} className="crwh-badge"><CRWIcon d={b.icon} size={12} />{b.label}</span>
              ))}
            </div>
          </div>
        </div>

        {/* ---- the live demo panel: real app structure, hero lighting ---- */}
        <div className="crwh-panelwrap">
          <div className="crwh-panel" ref={panelRef} aria-hidden="true">
            <div className="crwh-panel-in">
              <div className="crwh-panel-bar">
                <img src={logoMarkMono} width="12" height="12" alt="" style={{ opacity: 0.6 }} />
                <span className="crwh-panel-title">ClipRadar</span>
                <span className="crwh-panel-live"><i className="crw-pulse"></i>Online</span>
                <span className="crwh-panel-win"><i>—</i><i>□</i><i>✕</i></span>
              </div>
              <div className="crwh-panel-body">
                <div className="crwh-modes">
                  {(['Visuals', 'Speech'] as const).map((m) => (
                    <span key={m} className={`crwh-mode${(d?.mode ?? 'Visuals') === m ? ' crwh-mode-on' : ''}`}>
                      <CRWIcon d={m === 'Visuals' ? 'image' : 'mic'} size={11} />{m}
                    </span>
                  ))}
                </div>
                <div className={`crwh-bar${live ? ' crwh-bar-live' : ''}`}>
                  <span className="crwh-bar-chips">
                    <span className="crwh-bar-scope"><CRWIcon d="at" size={10} /> {d?.scope ?? 'All analyzed media'}</span>
                    <span className={`crwh-bar-people${d?.people ? ' crwh-bar-people-on' : ''}`}>
                      <CRWIcon d="user" size={10} /> {d?.people ?? 'People'}
                    </span>
                  </span>
                  <span key={active} className="crwh-bar-text crw-fade">
                    {d?.seed ? (
                      <span className="crwh-bar-seed">
                        <img src={d.seed} alt="" />
                        Similar to this frame
                        <i>✕</i>
                      </span>
                    ) : (
                      <>
                        {typed || <span className="crwh-bar-ph">Search your footage…</span>}
                        {typed && !done ? <span className="crw-caret" style={{ height: 12, width: 5 }}></span> : null}
                      </>
                    )}
                  </span>
                  <span className={`crwh-bar-cam${d?.seed ? ' crwh-bar-cam-on' : ''}`}><CRWIcon d="camera" size={13} /></span>
                  <span className="crwh-bar-go"><CRWIcon d="arrowRight" size={13} sw={2.2} /></span>
                </div>
                <div className="crwh-results-head">
                  <span className={`crwh-results-count${r ? ' crwh-results-count-on' : ''}`}>
                    {r ? r.count : 'Scanning library…'}
                  </span>
                  <span className="crwh-results-sort">Best match ↓</span>
                </div>
                <div className={`crwh-results${r?.speech ? ' crwh-results-rows' : ''}`} ref={resultsBox}>
                  {results}
                </div>
                <div className={`crwh-results-hint${r?.hint ? ' crwh-results-hint-on' : ''}`}>{r?.hint ?? ' '}</div>
              </div>
            </div>
          </div>
          {/* grounding glow under the panel */}
          <div className="crwh-panel-shadow" aria-hidden="true"></div>
          {/* the demo controls live OUTSIDE the app window — they're ours */}
          <div className="crwh-tries" role="group" aria-label="Try a search">
            <span className="crwh-try-label">Try</span>
            {DEMOS.map((dd, i) => (
              <button key={dd.chip} className={`crwh-try${live && active === i ? ' crwh-try-on' : ''}`} onClick={() => tryDemo(i)}>
                <CRWIcon d={dd.icon} size={11} />
                {dd.chip}
              </button>
            ))}
          </div>
        </div>
      </div>

      <div className="crwh-cue" aria-hidden="true">
        <span>Scroll</span>
        <i></i>
      </div>
    </header>
  );
}
